import { ReconciliationStatusBadge } from "@/components/reconciliation/reconciliation-status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { buildMetadata } from "@/lib/metadata";
import { cn } from "@/lib/utils";
import { ReconciliationService } from "@/services/reconciliation-service";
import { UserProfileService } from "@/services/user-profile-service";
import type {
  ReconciliationFile,
  ReconciliationInvoice,
} from "@/services/types/reconciliation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  CloudUpload,
  FileText,
  Loader2,
  Trash2,
} from "lucide-react";
import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";

type UploadState =
  | "queued"
  | "creating_run"
  | "uploading"
  | "uploaded"
  | "extracting"
  | "extracted"
  | "failed"
  | "removing";

type UploadItem = {
  id: string;
  file: File;
  status: UploadState;
  progress: number;
  error?: string;
  remoteFileId?: string;
};

const createUploadId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatFileSize = (file: File) => `${(file.size / 1024 / 1024).toFixed(1)} MB`;

const isUploadingState = (status: UploadState) =>
  status === "creating_run" || status === "uploading" || status === "removing";

const toUploadState = (processingStatus: string): UploadState => {
  if (processingStatus === "extracting") {
    return "extracting";
  }
  if (processingStatus === "extracted") {
    return "extracted";
  }
  if (processingStatus === "failed") {
    return "failed";
  }
  return "uploaded";
};

const ACTIVE_RUN_STATUSES = new Set([
  "processing",
  "invoice_processing",
  "uploading_bank_statements",
  "bank_statement_processing",
]);

export const Route = createFileRoute("/_main/reconciliation/new")({
  beforeLoad: async () => {
    const profile = await UserProfileService.getMine();
    if (!profile?.is_financial_reconciliation_enabled) {
      throw redirect({ to: "/" });
    }
  },
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "New reconciliation run",
      description: "Upload invoices, confirm they all arrive successfully, then start extraction.",
      keywords: ["new run", "reconciliation", "invoices", "invoice extraction"],
    }),
  }),
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const invoiceInputId = useId();
  const invoiceDropRef = useRef<HTMLLabelElement>(null);
  const processingUploadIdRef = useRef<string | null>(null);
  const rebuildInFlightRef = useRef(false);

  const [runId, setRunId] = useState<string | null>(null);
  const [invoiceFiles, setInvoiceFiles] = useState<UploadItem[]>([]);
  const [invoiceDragActive, setInvoiceDragActive] = useState(false);
  const [invoiceExtractionRequested, setInvoiceExtractionRequested] = useState(false);
  const hasLocalExtractingFiles = invoiceFiles.some(
    (item) => item.status === "extracting",
  );
  const hasTerminalInvoiceStates =
    invoiceFiles.length > 0 &&
    invoiceFiles.every((item) =>
      ["extracted", "failed"].includes(item.status),
    );
  const shouldPollInvoiceExtraction =
    invoiceExtractionRequested ||
    hasLocalExtractingFiles ||
    invoiceFiles.some((item) => item.status === "uploaded");

  const { data: run } = useQuery({
    queryKey: ["reconciliation-run", runId],
    queryFn: () => ReconciliationService.getRun(runId as string),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      if (shouldPollInvoiceExtraction) {
        return 4000;
      }
      const current = query.state.data;
      if (!current) {
        return false;
      }
      const invoicePending = current.invoice_files.some((file) =>
        ["uploaded", "extracting"].includes(file.processing_status),
      );
      return invoicePending ? 4000 : false;
    },
  });

  const invoiceExtractionStarted =
    hasLocalExtractingFiles ||
    Boolean(
      run?.invoice_files.some((file) =>
        ["extracting", "extracted", "failed"].includes(file.processing_status),
      ),
    );

  const { data: invoices = [] } = useQuery({
    queryKey: ["reconciliation-invoices", runId],
    queryFn: () => ReconciliationService.listInvoices(runId as string),
    enabled: Boolean(runId),
    refetchInterval: () =>
      runId && shouldPollInvoiceExtraction
        ? 4000
        : false,
  });

  const startInvoiceExtractionMutation = useMutation({
    mutationFn: () => ReconciliationService.startExtraction(runId as string),
    onSuccess: async () => {
      setInvoiceExtractionRequested(true);
      toast.success("Invoice extraction started");
      setInvoiceFiles((previous) =>
        previous.map((item) =>
          item.status === "uploaded"
            ? {
                ...item,
                status: "extracting",
                progress: Math.max(item.progress, 92),
                error: undefined,
              }
            : item,
        ),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reconciliation-run", runId] }),
        queryClient.invalidateQueries({ queryKey: ["reconciliation-invoices", runId] }),
        queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] }),
      ]);
    },
    onError: (error) => {
      setInvoiceExtractionRequested(false);
      setInvoiceFiles((previous) =>
        previous.map((item) =>
          item.status === "extracting" && !item.remoteFileId
            ? {
                ...item,
                status: "uploaded",
                progress: 100,
              }
            : item,
        ),
      );
      toast.error("Could not start invoice extraction", {
        description:
          error instanceof Error ? error.message : "Upload invoice files first.",
      });
    },
  });

  const filesReadyForExtraction =
    invoiceFiles.some((item) => item.status === "uploaded") &&
    invoiceFiles.every((item) =>
      ["uploaded", "extracted"].includes(item.status),
    );

  const uploadSummary = {
    uploaded: invoiceFiles.filter((item) => item.status === "uploaded").length,
    extracted: invoiceFiles.filter((item) => item.status === "extracted").length,
    inProgress: invoiceFiles.filter((item) =>
      ["queued", "creating_run", "uploading", "extracting"].includes(item.status),
    ).length,
    removing: invoiceFiles.filter((item) => item.status === "removing").length,
    failed: invoiceFiles.filter((item) => item.status === "failed").length,
  };
  const allInvoicesExtracted =
    invoiceFiles.length > 0 &&
    invoiceFiles.every((item) => item.status === "extracted");

  const queueFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    setInvoiceFiles((previous) => {
      const existing = new Set(
        previous.map(
          (item) => `${item.file.name}-${item.file.size}-${item.file.lastModified}`,
        ),
      );

      const next = files
        .filter((file) => {
          const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
          if (![".pdf", ".jpg", ".jpeg", ".png"].includes(extension)) {
            toast.error(`"${file.name}" is not a supported file type.`);
            return false;
          }

          const signature = `${file.name}-${file.size}-${file.lastModified}`;
          if (existing.has(signature)) {
            return false;
          }
          existing.add(signature);
          return true;
        })
        .map((file) => ({
          id: createUploadId(),
          file,
          status: "queued" as const,
          progress: 0,
        }));

      return [...previous, ...next];
    });
  };

  const syncRemoteFileToItem = (
    items: UploadItem[],
    file: File,
    remoteFiles: ReconciliationFile[],
  ) => {
    const takenIds = new Set(
      items
        .filter(
          (item) =>
            item.file !== file &&
            typeof item.remoteFileId !== "undefined" &&
            item.remoteFileId !== "",
        )
        .map((item) => item.remoteFileId),
    );

    return remoteFiles.find(
      (remoteFile) =>
        remoteFile.original_filename === file.name &&
        !takenIds.has(String(remoteFile.id)),
    );
  };

  const completedInvoiceSourceFileIds = new Set(
    invoices
      .filter(
        (invoice: ReconciliationInvoice) =>
          invoice.extraction_status === "completed" &&
          invoice.source_file_id !== null &&
          invoice.source_file_id !== undefined,
      )
      .map((invoice: ReconciliationInvoice) => String(invoice.source_file_id)),
  );
  const completedInvoiceSourceFileNames = new Set(
    invoices
      .filter(
        (invoice: ReconciliationInvoice) =>
          invoice.extraction_status === "completed" && Boolean(invoice.source_file_name),
      )
      .map((invoice: ReconciliationInvoice) => String(invoice.source_file_name)),
  );
  const failedInvoiceSourceFileIds = new Set(
    invoices
      .filter(
        (invoice: ReconciliationInvoice) =>
          invoice.extraction_status === "failed" &&
          invoice.source_file_id !== null &&
          invoice.source_file_id !== undefined,
      )
      .map((invoice: ReconciliationInvoice) => String(invoice.source_file_id)),
  );
  const failedInvoiceSourceFileNames = new Set(
    invoices
      .filter(
        (invoice: ReconciliationInvoice) =>
          invoice.extraction_status === "failed" && Boolean(invoice.source_file_name),
      )
      .map((invoice: ReconciliationInvoice) => String(invoice.source_file_name)),
  );

  const runStillProcessing = run
    ? ACTIVE_RUN_STATUSES.has(run.run_status)
    : false;

  useEffect(() => {
    if (!run && invoices.length === 0) {
      return;
    }

    setInvoiceFiles((previous) =>
      previous.map((item) => {
        const hasCompletedInvoiceRecord =
          (item.remoteFileId
            ? completedInvoiceSourceFileIds.has(item.remoteFileId)
            : false) ||
          completedInvoiceSourceFileNames.has(item.file.name);
        const hasFailedInvoiceRecord =
          (item.remoteFileId ? failedInvoiceSourceFileIds.has(item.remoteFileId) : false) ||
          failedInvoiceSourceFileNames.has(item.file.name);

        if (hasCompletedInvoiceRecord || hasFailedInvoiceRecord) {
          return {
            ...item,
            status: hasFailedInvoiceRecord ? "failed" : "extracted",
            progress: hasFailedInvoiceRecord ? item.progress : 100,
          };
        }

        if (!run) {
          return item;
        }

        const remoteFile =
          (item.remoteFileId
            ? run.invoice_files.find((file) => String(file.id) === item.remoteFileId)
            : undefined) ??
          syncRemoteFileToItem(previous, item.file, run.invoice_files);

        if (!remoteFile) {
          return item;
        }

        const hasCompletedRemoteInvoiceRecord =
          completedInvoiceSourceFileIds.has(String(remoteFile.id)) ||
          completedInvoiceSourceFileNames.has(remoteFile.original_filename) ||
          completedInvoiceSourceFileNames.has(item.file.name);
        const hasFailedRemoteInvoiceRecord =
          failedInvoiceSourceFileIds.has(String(remoteFile.id)) ||
          failedInvoiceSourceFileNames.has(remoteFile.original_filename) ||
          failedInvoiceSourceFileNames.has(item.file.name);
        const shouldStayExtracting =
          invoiceExtractionRequested &&
          !hasCompletedRemoteInvoiceRecord &&
          !hasFailedRemoteInvoiceRecord &&
          ["uploaded", "extracting"].includes(item.status) &&
          remoteFile.processing_status === "uploaded";
        const shouldForceExtracted =
          hasCompletedRemoteInvoiceRecord ||
          (!runStillProcessing &&
            item.status === "extracting" &&
            remoteFile.processing_status === "uploaded");
        const nextStatus = hasFailedRemoteInvoiceRecord
          ? "failed"
          : shouldStayExtracting
            ? "extracting"
          : shouldForceExtracted
            ? "extracted"
            : toUploadState(remoteFile.processing_status);

        return {
          ...item,
          remoteFileId: String(remoteFile.id),
          status: nextStatus,
          progress:
            nextStatus === "extracted"
              ? 100
              : nextStatus === "extracting"
                ? Math.max(Math.min(item.progress, 96), 92)
                : item.progress,
          error: remoteFile.processing_error ?? item.error,
        };
      }),
    );
  }, [
    completedInvoiceSourceFileIds,
    completedInvoiceSourceFileNames,
    failedInvoiceSourceFileIds,
    failedInvoiceSourceFileNames,
    invoices.length,
    run,
    runStillProcessing,
    invoiceExtractionRequested,
  ]);

  useEffect(() => {
    if (!invoiceExtractionRequested || !hasTerminalInvoiceStates) {
      return;
    }

    setInvoiceExtractionRequested(false);
  }, [hasTerminalInvoiceStates, invoiceExtractionRequested]);

  useEffect(() => {
    const hasUploadingItems = invoiceFiles.some((item) =>
      ["creating_run", "uploading", "extracting"].includes(item.status),
    );
    if (!hasUploadingItems) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setInvoiceFiles((previous) =>
        previous.map((item) => {
          if (!["creating_run", "uploading", "extracting"].includes(item.status)) {
            return item;
          }

          if (item.status === "extracting") {
            const nextProgress = Math.min(item.progress + 2, 96);
            return {
              ...item,
              progress: nextProgress,
            };
          }

          return {
            ...item,
            progress: Math.min(item.progress + 9, 88),
          };
        }),
      );
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [invoiceFiles]);

  useEffect(() => {
    const nextItem = invoiceFiles.find((item) => item.status === "queued");
    if (!nextItem || processingUploadIdRef.current || rebuildInFlightRef.current) {
      return;
    }

    processingUploadIdRef.current = nextItem.id;
    setInvoiceFiles((previous) =>
      previous.map((item) =>
        item.id === nextItem.id
          ? {
              ...item,
              status: runId ? "uploading" : "creating_run",
              progress: 12,
              error: undefined,
            }
          : item,
      ),
    );

    void (async () => {
      try {
        const response = runId
          ? await ReconciliationService.uploadInvoices(runId, [nextItem.file])
          : await ReconciliationService.createRun([], [nextItem.file]);

        const nextRunId = String(response.id);
        setRunId(nextRunId);

        setInvoiceFiles((previous) => {
          const matchedRemoteFile = syncRemoteFileToItem(
            previous,
            nextItem.file,
            response.invoice_files,
          );

          return previous.map((item) =>
            item.id === nextItem.id
              ? {
                  ...item,
                  status: "uploaded",
                  progress: 100,
                  error: undefined,
                  remoteFileId: matchedRemoteFile ? String(matchedRemoteFile.id) : undefined,
                }
              : item,
          );
        });

        await queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] });
      } catch (error) {
        setInvoiceFiles((previous) =>
          previous.map((item) =>
            item.id === nextItem.id
              ? {
                  ...item,
                  status: "failed",
                  progress: 0,
                  error:
                    error instanceof Error
                      ? error.message
                      : "The file could not be uploaded.",
                }
              : item,
          ),
        );

        toast.error(`Failed to upload "${nextItem.file.name}"`, {
          description:
            error instanceof Error ? error.message : "Please try the file again.",
        });
      } finally {
        processingUploadIdRef.current = null;
      }
    })();
  }, [invoiceFiles, queryClient, runId]);

  const rebuildRun = async (items: UploadItem[], previousItems: UploadItem[]) => {
    const nextItems = items.map((item) => ({
      ...item,
      status: "queued" as const,
      progress: 0,
      error: undefined,
      remoteFileId: undefined,
    }));

    rebuildInFlightRef.current = true;
    processingUploadIdRef.current = null;
    setInvoiceFiles(nextItems);

    try {
      if (runId) {
        await ReconciliationService.deleteRun(runId);
      }
      setRunId(null);
      await queryClient.invalidateQueries({ queryKey: ["reconciliation-runs"] });
      if (nextItems.length === 0) {
        toast.success("Invoice removed");
      } else {
        toast.success("Invoice removed", {
          description: "Rebuilding the draft run with the remaining files.",
        });
      }
    } catch (error) {
      toast.error("Could not remove invoice", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
      setInvoiceFiles(previousItems);
    } finally {
      rebuildInFlightRef.current = false;
    }
  };

  const removeInvoice = async (itemId: string) => {
    const target = invoiceFiles.find((item) => item.id === itemId);
    if (!target) {
      return;
    }

    if (target.status === "queued" || target.status === "failed") {
      setInvoiceFiles((previous) => previous.filter((item) => item.id !== itemId));
      return;
    }

    if (
      isUploadingState(target.status) ||
      target.status === "extracting" ||
      target.status === "extracted"
    ) {
      return;
    }

    const previousItems = invoiceFiles;

    setInvoiceFiles((previous) =>
      previous.map((item) =>
        item.id === itemId ? { ...item, status: "removing" } : item,
      ),
    );

    await rebuildRun(
      invoiceFiles.filter((item) => item.id !== itemId),
      previousItems,
    );
  };

  const remoteFilesById = new Map(
    (run?.invoice_files ?? []).map((file) => [String(file.id), file]),
  );

  return (
    <div className="space-y-8 p-4 pb-24 lg:p-8 lg:pb-8">
      <Link
        to="/reconciliation"
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Financial Reconciliation
      </Link>

      <div className="space-y-2">
        <h1 className="text-center text-3xl tracking-tight lg:text-left lg:text-4xl">
          New reconciliation run
        </h1>
        <p className="max-w-4xl text-center text-slate-500 lg:text-left">
          Upload your vendor invoices. Each file starts uploading immediately, and
          extraction can begin once every selected invoice has been uploaded
          successfully.
        </p>
      </div>

      <div className="mx-auto max-w-6xl space-y-6">
        <UploadZone
          ref={invoiceDropRef}
          inputId={invoiceInputId}
          title="Drag & drop invoices here"
          subtitle="PDF, JPG, JPEG or PNG. Invoices upload automatically as soon as you select them."
          browseLabel="Browse files"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/png,image/jpeg"
          dragActive={invoiceDragActive}
          setDragActive={setInvoiceDragActive}
          onFiles={queueFiles}
        />

        <InvoiceQueueCard
          title={`Files to process (${invoiceFiles.length})`}
          items={invoiceFiles}
          remoteFilesById={remoteFilesById}
          uploadedCount={uploadSummary.uploaded}
          extractedCount={uploadSummary.extracted}
          inProgressCount={uploadSummary.inProgress}
          removingCount={uploadSummary.removing}
          failedCount={uploadSummary.failed}
          extractionStarted={invoiceExtractionStarted}
          onRemove={(id) => void removeInvoice(id)}
          footerAction={
            <div className="flex items-center gap-3">
              {allInvoicesExtracted && runId ? (
                <Button asChild variant="outline" className="rounded-xl px-6">
                  <Link to="/reconciliation/$runId" params={{ runId }}>
                    Invoice Detail
                  </Link>
                </Button>
              ) : (
                <Button variant="outline" disabled className="rounded-xl px-6">
                  Invoice Detail
                </Button>
              )}
              <Button
                onClick={() => startInvoiceExtractionMutation.mutate()}
                disabled={
                  !runId ||
                  !filesReadyForExtraction ||
                  startInvoiceExtractionMutation.isPending ||
                  hasLocalExtractingFiles
                }
                className="rounded-xl bg-black px-6 text-white hover:bg-black/90"
              >
                {startInvoiceExtractionMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Starting
                  </>
                ) : (
                  "Start invoice extraction"
                )}
              </Button>
            </div>
          }
        />
        <div className="space-y-3 lg:hidden">
          {invoiceFiles.map((item) => (
            <MobileUploadCard
              key={item.id}
              item={item}
              remoteFile={
                item.remoteFileId ? remoteFilesById.get(item.remoteFileId) : undefined
              }
              extractionStarted={invoiceExtractionStarted}
              onRemove={(id) => void removeInvoice(id)}
            />
          ))}
        </div>

        {allInvoicesExtracted ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            Invoice extraction is complete. Click the Invoice Detail button to
            check the extracted invoices.
          </div>
        ) : hasLocalExtractingFiles ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
            Invoice extraction is in progress. File statuses will change to Extracted
            here when processing finishes.
          </div>
        ) : !filesReadyForExtraction ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Start invoice extraction is enabled only when every selected file has
            uploaded successfully.
          </div>
        ) : null}
      </div>

      <div className="fixed right-4 bottom-4 left-4 z-20 pb-[max(env(safe-area-inset-bottom),0px)] lg:hidden">
        <div className="grid grid-cols-2 gap-3">
          {allInvoicesExtracted && runId ? (
            <Button asChild variant="outline" className="h-12 rounded-xl bg-white">
              <Link to="/reconciliation/$runId" params={{ runId }}>
                Invoice Detail
              </Link>
            </Button>
          ) : (
            <Button variant="outline" disabled className="h-12 rounded-xl bg-white">
              Invoice Detail
            </Button>
          )}
          <Button
            onClick={() => startInvoiceExtractionMutation.mutate()}
            disabled={
              !runId ||
              !filesReadyForExtraction ||
              startInvoiceExtractionMutation.isPending ||
              hasLocalExtractingFiles
            }
            className="h-12 rounded-xl bg-black text-white hover:bg-black/90"
          >
            {startInvoiceExtractionMutation.isPending ? "Starting..." : "Start extraction"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const UploadZone = forwardRef<
  HTMLLabelElement,
  {
    inputId: string;
    title: string;
    subtitle: string;
    browseLabel: string;
    accept: string;
    dragActive: boolean;
    setDragActive: (active: boolean) => void;
    onFiles: (files: File[]) => void;
  }
>(function UploadZone(
  {
    inputId,
    title,
    subtitle,
    browseLabel,
    accept,
    dragActive,
    setDragActive,
    onFiles,
  },
  ref,
) {
  return (
    <label
      ref={ref}
      htmlFor={inputId}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        if (
          ref &&
          typeof ref === "object" &&
          ref.current &&
          !ref.current.contains(event.relatedTarget as Node)
        ) {
          setDragActive(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        onFiles(Array.from(event.dataTransfer.files ?? []));
      }}
      className={cn(
        "flex min-h-[260px] cursor-pointer flex-col items-center justify-center rounded-[24px] border-2 border-dashed px-6 py-10 text-center transition-colors md:min-h-[300px]",
        dragActive ? "border-lime-300 bg-lime-50" : "border-slate-200 bg-white",
      )}
    >
      <div className="flex size-20 items-center justify-center rounded-full bg-slate-50">
        <CloudUpload className="size-9 text-slate-500" />
      </div>
      <h2 className="mt-6 text-xl md:text-2xl">{title}</h2>
      <p className="mt-3 max-w-2xl text-slate-500">{subtitle}</p>
      <div className="mt-6 rounded-xl bg-black px-6 py-3 text-sm text-white">
        {browseLabel}
      </div>
      <p className="mt-5 text-sm text-slate-400">
        PDF, JPG or PNG. Upload starts immediately after selection.
      </p>
      <input
        id={inputId}
        type="file"
        className="sr-only"
        accept={accept}
        multiple
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
    </label>
  );
});

function InvoiceQueueCard({
  title,
  items,
  remoteFilesById,
  uploadedCount,
  extractedCount,
  inProgressCount,
  removingCount,
  failedCount,
  extractionStarted,
  onRemove,
  footerAction,
}: {
  title: string;
  items: UploadItem[];
  remoteFilesById: Map<string, ReconciliationFile>;
  uploadedCount: number;
  extractedCount: number;
  inProgressCount: number;
  removingCount: number;
  failedCount: number;
  extractionStarted: boolean;
  onRemove: (id: string) => void;
  footerAction: React.ReactNode;
}) {
  return (
    <Card className="hidden rounded-[24px] border-slate-200 py-0 shadow-none lg:block">
      <CardHeader className="flex flex-row items-center justify-between gap-4 px-5 pt-5">
        <CardTitle className="text-lg">{title}</CardTitle>
        <div className="text-sm text-slate-400">
          {uploadedCount > 0 ? `${uploadedCount} uploaded` : "0 uploaded"}
          {extractedCount > 0 ? ` - ${extractedCount} extracted` : ""}
          {inProgressCount > 0 ? ` - ${inProgressCount} processing` : ""}
          {removingCount > 0 ? ` - ${removingCount} removing` : ""}
          {failedCount > 0 ? ` - ${failedCount} failed` : ""}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 px-5 pb-5">
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
            No files selected yet.
          </div>
        ) : null}

        {items.map((item) => {
          const remoteFile = item.remoteFileId
            ? remoteFilesById.get(item.remoteFileId)
            : undefined;
          const remoteStatus = remoteFile
            ? toUploadState(remoteFile.processing_status)
            : undefined;
          const useRemoteStatusBadge =
            remoteFile &&
            remoteStatus === item.status &&
            !["queued", "creating_run", "uploading", "removing"].includes(item.status);

          return (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3"
            >
              <div className="rounded-lg bg-slate-100 p-3">
                <FileText className="size-5 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{item.file.name}</div>
                <div className="text-sm text-slate-400">
                  {formatFileSize(item.file)} - {item.file.name.split(".").pop()?.toUpperCase()}
                </div>
              </div>

              {useRemoteStatusBadge ? (
                <ReconciliationStatusBadge status={remoteFile.processing_status} />
              ) : (
                <UploadStateIndicator
                  status={item.status}
                  progress={item.progress}
                  error={item.error}
                />
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemove(item.id)}
                disabled={isUploadingState(item.status) || extractionStarted}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          );
        })}

        <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-4">
          <div className="text-sm text-slate-500">
            {items.length} file{items.length === 1 ? "" : "s"} selected
            {uploadedCount > 0 ? ` - ${uploadedCount} uploaded` : ""}
            {extractedCount > 0 ? ` - ${extractedCount} extracted` : ""}
          </div>
          {footerAction}
        </div>
      </CardContent>
    </Card>
  );
}

function MobileUploadCard({
  item,
  remoteFile,
  extractionStarted,
  onRemove,
}: {
  item: UploadItem;
  remoteFile?: ReconciliationFile;
  extractionStarted: boolean;
  onRemove: (id: string) => void;
}) {
  const remoteStatus = remoteFile
    ? toUploadState(remoteFile.processing_status)
    : undefined;
  const useRemoteStatusBadge =
    remoteFile &&
    remoteStatus === item.status &&
    !["queued", "creating_run", "uploading", "removing"].includes(item.status);

  return (
    <div className="flex items-center gap-3 rounded-[16px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="rounded-lg bg-slate-100 p-3">
        <FileText className="size-5 text-slate-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-900">{item.file.name}</div>
        <div className="text-sm text-slate-400">{formatFileSize(item.file)}</div>
      </div>
      <div className="shrink-0">
        {useRemoteStatusBadge ? (
          <ReconciliationStatusBadge status={remoteFile.processing_status} />
        ) : (
          <UploadStateIndicator
            status={item.status}
            progress={item.progress}
            error={item.error}
          />
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(item.id)}
        disabled={isUploadingState(item.status) || extractionStarted}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

function UploadStateIndicator({
  status,
  progress,
  error,
}: {
  status: UploadState;
  progress: number;
  error?: string;
}) {
  if (status === "uploaded") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-700">
        <CheckCircle2 className="size-4 text-emerald-500" />
        Uploaded
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="max-w-48 text-right text-sm text-rose-600" title={error}>
        Upload failed
      </div>
    );
  }

  if (status === "queued") {
    return <div className="text-sm text-slate-400">Queued</div>;
  }

  if (status === "extracted") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-700">
        <CheckCircle2 className="size-4 text-emerald-500" />
        Extracted
      </div>
    );
  }

  if (status === "removing") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="size-4 animate-spin" />
        Removing
      </div>
    );
  }

  return (
    <div className="flex min-w-40 items-center gap-3">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-amber-300 transition-[width] duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="min-w-10 text-sm text-slate-500">
        {status === "creating_run"
          ? "..."
          : `${progress}%`}
      </div>
    </div>
  );
}
